import clsx from 'clsx'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Icon } from 'components/ui/Icon'

export type StatusChipVariant = 'info' | 'positive' | 'warning' | 'negative' | 'neutral'

/** Foreground / light-tint background per variant (shared so callers can reuse the colors). */
export const STATUS_CHIP_STYLES: Record<StatusChipVariant, { fg: string; bg: string }> = {
  info: { fg: '#2563eb', bg: 'rgba(37,99,235,0.1)' },
  positive: { fg: '#16a34a', bg: 'rgba(22,163,74,0.1)' },
  warning: { fg: '#ea580c', bg: 'rgba(234,88,12,0.1)' },
  negative: { fg: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
  neutral: { fg: 'var(--text-muted)', bg: 'var(--input-bg)' },
}

type StatusChipProps = {
  variant?: StatusChipVariant
  label?: string
  icon?: IconProp
  /** Render the label monospaced (for methods, keys, identifiers). */
  mono?: boolean
  /** Icon-only circular badge (no label/padding) instead of a pill. */
  circle?: boolean
  className?: string
}

/** Small semantic status pill (light tint + strong ink). Reusable across the widget. */
export const StatusChip = ({ variant = 'neutral', label, icon, mono, circle, className }: StatusChipProps) => {
  const style = STATUS_CHIP_STYLES[variant]

  if (circle) {
    return (
      <span
        className={clsx(
          'tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-flex-shrink-0',
          className,
        )}
        style={{ backgroundColor: style.bg, color: style.fg }}
      >
        {icon && <Icon icon={icon} className="tw-h-3 tw-w-3" />}
      </span>
    )
  }

  return (
    <span
      className={clsx(
        'tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold',
        mono && 'tw-font-mono',
        className,
      )}
      style={{ backgroundColor: style.bg, color: style.fg }}
    >
      {icon && <Icon icon={icon} className="tw-h-2.5 tw-w-2.5" />}
      {label}
    </span>
  )
}
